import { useState, useEffect } from 'react'

import css from './sass/createedit.module.scss'
import themeCss from '../components/sass/theme.module.scss'

export default function AuctionEditPermissions({ permissions, setPermissions, isReadOnly, auctionChanges }) {
  const inputValue = (value) => value ?? ''

  //emails and levels state variables are used for the display of the permissions from the parent component
  const [emails, setEmails] = useState(permissions.length > 0 ? permissions.map(ele => ele.Email ? ele.Email : '') : [""])
  const [levels, setLevels] = useState(permissions.length > 0 ? permissions.map(ele => ele.Level ? ele.Level : '') : [""])
  // track when we've loaded from auctionChanges to prevent sync conflicts
  const [loadedFromAuctionChanges, setLoadedFromAuctionChanges] = useState(false)

  const [ownerAgreeCheck, setOwnerAgreeCheck] = useState(false)

  const updateEmailDisplay = (index, newValue) => {
    const newEmails = [...emails]
    newEmails[index] = newValue
    setEmails(newEmails)
  }

  const updateLevelDisplay = (index, newValue) => {
    const newLevels = [...levels]
    newLevels[index] = newValue
    setLevels(newLevels)
  }

  //set permissions to unsaved auction change values
  useEffect(() => {

    // If there is a non-null value in auctionChanges, set permissions to auctionChanges.permissions
    if (auctionChanges !== null && auctionChanges?.permissions) {
      setPermissions([...auctionChanges.permissions])
      // Sync local state arrays when loading from auctionChanges to prevent conflicts
      // This ensures emails and levels arrays match the restored permissions from session storage
      const restoredEmails = auctionChanges.permissions.map(ele => ele.Email ? ele.Email : '')
      const restoredLevels = auctionChanges.permissions.map(ele => ele.Level ? ele.Level : '')
      setEmails(restoredEmails)
      setLevels(restoredLevels)
      setLoadedFromAuctionChanges(true)
      return // Stop here if this condition is met
    }

  }, [auctionChanges, setPermissions])

  useEffect(() => {
    if (permissions.length !== 0) setOwnerAgreeCheck(true)
  }, [permissions])

  const removePermission = (index) => {
    const newPermissions = [...permissions]
    const newEmails = [...emails]
    const newLevels = [...levels]

    newPermissions.splice(index, 1)
    newEmails.splice(index, 1)
    newLevels.splice(index, 1)

    setPermissions(newPermissions)
    setEmails(newEmails)
    setLevels(newLevels)
  }

  const addNewPermission = () => {
    const newEmails = [...emails, '']
    const newLevels = [...levels, '']
    const newPermissions = [...permissions]
    newPermissions.push({ 'Email': '', 'Level': '' })
    setPermissions(newPermissions)
    setEmails(newEmails)
    setLevels(newLevels)
  }

  useEffect(() => {

    // Skip this useEffect if we just loaded from auctionChanges to preserve session storage data
    // This prevents overwriting the restored permissions from session storage
    if (loadedFromAuctionChanges) {
      setLoadedFromAuctionChanges(false)
      return
    }

    const updatePermissions = () => {
      const newPermissions = [...permissions]
      for (let i = 0; i < permissions.length; i++) {
        newPermissions[i] = { Email: emails[i], Level: levels[i] }
      }
      setPermissions(newPermissions)
    }

    //trying to fix on loading when permissions has a different length than emails or levels
    if (permissions.length !== emails.length) {
      let newEmails = []
      let newLevels = []

      for (let i = 0; i < permissions.length; i++) {
        newEmails.push(permissions[i].Email)
        newLevels.push(permissions[i].Level)
      }

      setEmails(newEmails)
      setLevels(newLevels)
    }

    else {
      for (let i = 0; i < permissions.length; i++) {

        if (emails[i] !== permissions[i].Email || levels[i] !== permissions[i].Level) updatePermissions()
      }
    }


  }, [emails, levels, permissions, setPermissions, loadedFromAuctionChanges])

  const validateClass = (field, index) => {
    if (field === 'email') {
      if (emails[index]?.includes('@')) return ''
      else return css['edit-permissions-invalid']
    }
    if (field === 'level') {
      if (levels[index] === '') return css['edit-permissions-invalid']
      else return ''
    }
  }

  return (
    <div className={css['create-item-input-outlined']}>
      <label>Collaborators (can view or edit this auction)</label>
      <p className={css['edit-permissions-notice']}>You will be responsible for all actions taken by Alternate Admin Users you assign. 
        Please ensure they follow all confidentiality rules and best practices.</p>

        {permissions?.length === 0 &&<label>
          <input
            type="checkbox"
            checked={ownerAgreeCheck}
            onChange={(e) => setOwnerAgreeCheck(e.target.checked)}
            disabled={isReadOnly}
          />
          I agree
        </label>}

      {permissions?.length > 0 &&
        <>
          <p>To add a new collaborator: </p>
          <ul className={css['edit-permissions-instructions']}>
            <li>Click Add (Another) Collaborator</li>
            <li>Fill out email address and assign View/Edit Permissions</li>
            <li>Click Save Auction at the bottom of the page when finished</li>
          </ul>
        </>
      }

      <div className={css['edit-permissions-grid']}>
        {permissions?.length > 0 &&
          <div className={css['edit-permissions-row-header']}>
            <p>Email</p>
            <p>Permission</p>
          </div>}

        {permissions?.length > 0 && permissions.map((permission, index) => (
          <div className={css['edit-permissions-row']}>
            <input type='email'
              className={validateClass('email', index)}
              placeholder='email@ucla.edu'
              value={inputValue(emails[index])}
              name='permissionsEmail'
              disabled={isReadOnly}
              onChange={(e) => updateEmailDisplay(index, e.target.value)} />

            <select value={inputValue(levels[index])} name='permissionsLevel' disabled={isReadOnly}
              className={validateClass('level', index)}
              onChange={(e) => updateLevelDisplay(index, e.target.value)}>
              <option value="">Select</option>
              <option value="View">View</option>
              <option value="Edit">Edit</option>
            </select>

            <button className={`${themeCss['StrokeRed']} ${themeCss['button']}`}
              disabled={isReadOnly} onClick={() => removePermission(index)}>Remove</button>
          </div>
        ))}

      </div>
      {!(permissions?.length) && <p className={css['edit-permissions-none']}>No collaborators assigned.</p>}
      {!isReadOnly && <button className={`${themeCss['FilledBlue']} ${themeCss['button']} ${css['edit-permissions-center']}`} 
        disabled={!ownerAgreeCheck}
        onClick={() => addNewPermission()}>
        {permissions?.length > 0 ? 'Add Another Collaborator' : 'Add Collaborator'}
      </button>}
    </div>
  )
}
